import type { GrokbotToolState } from "@repo/common";
import { getGrokbotToolSetup, setCompanyGrokbotAvailability } from "@repo/ui";
import { Bot, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";

function stateLabel(state: GrokbotToolState): string {
	switch (state) {
		case "not_available":
			return "Not available";
		case "available_not_configured":
			return "Available, not configured";
		case "available_configured":
			return "Configured";
		case "configuration_error":
			return "Configuration error";
	}
}

export function CompanyGrokbotAvailabilityCard({
	companyId,
}: {
	companyId: string;
}) {
	const [state, setState] = useState<GrokbotToolState>();
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);

	const loadState = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await getGrokbotToolSetup({ companyId });
			setState(result.data.state);
		} catch (error) {
			console.error("Failed to load Grokbot availability", error);
			toast.error("Failed to load Grokbot availability.");
		} finally {
			setIsLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		void loadState();
	}, [loadState]);

	const setAvailability = async (available: boolean) => {
		setIsUpdating(true);
		try {
			await setCompanyGrokbotAvailability({ companyId, available });
			await loadState();
			toast.success(
				available
					? "Grokbot is now available to this company."
					: "Grokbot is no longer available to this company.",
			);
		} catch (error) {
			console.error("Failed to update Grokbot availability", error);
			toast.error("Failed to update Grokbot availability.");
		} finally {
			setIsUpdating(false);
		}
	};

	const available = state !== undefined && state !== "not_available";

	return (
		<Card>
			<CardHeader>
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="space-y-1.5">
						<CardTitle className="flex items-center gap-2">
							<Bot className="h-5 w-5" />
							Grokbot
						</CardTitle>
						<CardDescription>
							Control whether this company can configure and assign the Grokbot
							tool.
						</CardDescription>
					</div>
					{state && <Badge variant="secondary">{stateLabel(state)}</Badge>}
				</div>
			</CardHeader>
			<CardContent>
				{isLoading && !state ? (
					<div className="flex items-center gap-2 text-sm text-slate-500">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading Grokbot availability...
					</div>
				) : state ? (
					<Button
						variant={available ? "outline" : "default"}
						onClick={() => void setAvailability(!available)}
						disabled={isUpdating || isLoading}
					>
						{isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{available ? "Disable for company" : "Enable for company"}
					</Button>
				) : (
					<Button variant="outline" onClick={() => void loadState()}>
						Retry
					</Button>
				)}
			</CardContent>
		</Card>
	);
}
